import {
  faCheck,
  faMagnifyingGlass,
  faRotate,
  faXmark
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isPositiveNumber, reduce } from "et3-type-guards";
import React from "react";

import configuration from "../../../../../configuration/keys";
import { EApprovalDecision } from "../../../../../constants/enum/approval-decision";
import { EApprovalItemStatus } from "../../../../../constants/enum/approval-item-status";
import { EApprovalPictureStatus } from "../../../../../constants/enum/approval-picture-status";
import i18n from "../../../../../constants/i18n";
import { PICTURE_IS_DELETED } from "../../../../../constants/input-entries-constants";
import Et3CustomizedButton from "../../../../../core/et3-button/Et3CustomizedButton";
import ET3Div from "../../../../../core/et3-div";
import { withLiteObserverAndTheme } from "../../../../../core/et3-enhanced-render";
import Et3Image from "../../../../../core/et3-Image";
import { SURFACE_BORDER } from "../../../../../core/et3-theme/surface-constants";
import getApproveRejectStore from "../../../../../stores/approval-reject-store";
import { TApprovalAlbumPicture, TDescriptionType } from "../../../../../Types";
import ApprovalStatusChip, {
  getStatusToneColor
} from "../../ApprovalStatusChip";
import getApprovalViewerStore from "../../../../../stores/approval-reject-store/approval-viewer-store";
import {
  TILE_ACTION_GAP,
  TILE_ACTION_ICON,
  TILE_ACTION_HEIGHT,
  TILE_ACTION_RADIUS_CSS,
  TILE_BADGE_OFFSET,
  TILE_RADIUS_CSS,
  TILE_SIZE,
  TILE_ZOOM_BOX,
  TILE_ZOOM_ICON
} from "./lane-constants";

const PICTURE_ACTION_TYPE: TDescriptionType = "not_description";

export const getPictureStatus = (picture: TApprovalAlbumPicture) => {
  const isActive = reduce(() => picture.isActive, 0);
  if (isActive === PICTURE_IS_DELETED) {
    return EApprovalItemStatus.DELETED;
  }
  const status = reduce(() => `${picture.status}`, "");
  switch (status) {
    case EApprovalPictureStatus.APPROVED:
      return EApprovalItemStatus.APPROVED;
    case EApprovalPictureStatus.REJECTED:
      return EApprovalItemStatus.REJECTED;
    default:
      return EApprovalItemStatus.PENDING;
  }
};

const PictureTile = withLiteObserverAndTheme<{
  picture: TApprovalAlbumPicture;
  userId: number;
  userUUID: string;
}>((props) => {
  const { picture, userId, userUUID, theme } = props;
  const { colors, unitX, unitY } = theme;
  const fallbackImage =
    require("../../../../../assets/images/no-image.png").default;
  const pictureName = reduce(() => `${picture.picture}`, "");
  const pictureSource = reduce(
    () => `${configuration.file_center}${pictureName}`,
    fallbackImage
  );
  const pictureId = reduce(() => `${picture.id}`, "");
  const status = getPictureStatus(picture);
  const isPending = status === EApprovalItemStatus.PENDING;
  const isDeleted = status === EApprovalItemStatus.DELETED;
  const toneColor = getStatusToneColor(status, theme);
  const pictureActionId = reduce(() => picture.id, 0);
  const isChangeStatusPressed = getApproveRejectStore().getActionStatus(
    PICTURE_ACTION_TYPE,
    pictureActionId
  );

  const decide = async (decision: EApprovalDecision) => {
    const store = getApproveRejectStore();
    const buttonId = `${userId}-${decision}Picture-${pictureId}`;
    if (decision === EApprovalDecision.APPROVED) {
      await store.approveUserPicture(
        userId,
        userUUID,
        "APPROVAL_DATA",
        pictureId,
        isPending,
        buttonId
      );
    } else {
      await store.rejectUserPicture(
        userId,
        userUUID,
        "APPROVAL_DATA",
        pictureId,
        isPending,
        buttonId
      );
    }
    store.registerDecision();
  };

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={TILE_SIZE}
      height={0}
      fDirection="column"
      alignItems="stretch"
      justifyContent="flex-start"
      color={colors.all.transparent}
      style={{ flexShrink: 0, gap: TILE_ACTION_GAP * unitY }}
      onClick={() => {}}
    >
      <ET3Div
        isScrollable={false}
        isExtendable={false}
        withShadow={false}
        width={TILE_SIZE}
        height={0}
        fDirection="column"
        alignItems="flex-start"
        justifyContent="flex-start"
        color={colors.all.paleBluishGray}
        style={{
          position: "relative",
          height: TILE_SIZE * unitX,
          minHeight: TILE_SIZE * unitX,
          boxSizing: "border-box",
          borderRadius: TILE_RADIUS_CSS,
          border: `1px solid ${toneColor}`,
          overflow: "hidden",
          cursor: isDeleted ? "default" : "zoom-in"
        }}
        onClick={() => {
          if (!isDeleted) {
            getApprovalViewerStore().openPicture(pictureSource);
          }
        }}
      >
        <Et3Image
          type="ONLINE"
          alt={i18n.get("PICTURE")}
          imageName={pictureSource}
          size={TILE_SIZE}
          onClick={() => {
            if (!isDeleted) {
              getApprovalViewerStore().openPicture(pictureSource);
            }
          }}
          style={{
            width: TILE_SIZE * unitX,
            height: TILE_SIZE * unitX,
            objectFit: "cover",
            filter: isDeleted ? "grayscale(1)" : "none",
            opacity: isDeleted ? 0.45 : 1
          }}
        />
        {isDeleted ? null : (
          <ET3Div
            isScrollable={false}
            isExtendable={false}
            withShadow={false}
            width={TILE_ZOOM_BOX}
            height={TILE_ZOOM_BOX}
            fDirection="row"
            alignItems="center"
            justifyContent="center"
            color={colors.all.white}
            style={{
              position: "absolute",
              right: TILE_BADGE_OFFSET * unitX,
              bottom: TILE_BADGE_OFFSET * unitY,
              border: SURFACE_BORDER,
              borderRadius: TILE_ACTION_RADIUS_CSS
            }}
            onClick={() => {}}
          >
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              color={colors.all.davysGrey}
              title={i18n.get("ENLARGE_PICTURE")}
              style={{
                width: TILE_ZOOM_ICON * unitX,
                height: TILE_ZOOM_ICON * unitY
              }}
            />
          </ET3Div>
        )}
      </ET3Div>

      <ET3Div
        isScrollable={false}
        isExtendable={false}
        withShadow={false}
        width={TILE_SIZE}
        height={TILE_ACTION_HEIGHT}
        fDirection="row"
        alignItems="center"
        justifyContent="flex-start"
        color={colors.all.transparent}
        style={{ gap: TILE_ACTION_GAP * unitX }}
        onClick={() => {}}
      >
        <ApprovalStatusChip status={status} />
        {isDeleted ? null : (
          <React.Fragment>
            {isPending ? (
              <Et3CustomizedButton
                isEnabled={true}
                withShadow={false}
                width={0}
                height={TILE_ACTION_HEIGHT}
                fDirection="row"
                alignItems="center"
                justifyContent="center"
                color={colors.all.red}
                style={{
                  flex: 1,
                  width: "auto",
                  minWidth: 0,
                  borderRadius: TILE_ACTION_RADIUS_CSS,
                  cursor: "pointer"
                }}
                onClick={async () => await decide(EApprovalDecision.REJECTED)}
              >
                <FontAwesomeIcon
                  icon={faXmark}
                  color={colors.all.white}
                  title={i18n.get("REJECT_PICTURE")}
                  style={{
                    width: TILE_ACTION_ICON * unitX,
                    height: TILE_ACTION_ICON * unitY
                  }}
                />
              </Et3CustomizedButton>
            ) : null}
            {isPending ? (
              <Et3CustomizedButton
                isEnabled={true}
                withShadow={false}
                width={0}
                height={TILE_ACTION_HEIGHT}
                fDirection="row"
                alignItems="center"
                justifyContent="center"
                color={colors.all.green}
                style={{
                  flex: 1,
                  width: "auto",
                  minWidth: 0,
                  borderRadius: TILE_ACTION_RADIUS_CSS,
                  cursor: "pointer"
                }}
                onClick={async () => await decide(EApprovalDecision.APPROVED)}
              >
                <FontAwesomeIcon
                  icon={faCheck}
                  color={colors.all.white}
                  title={i18n.get("APPROVE_PICTURE")}
                  style={{
                    width: TILE_ACTION_ICON * unitX,
                    height: TILE_ACTION_ICON * unitY
                  }}
                />
              </Et3CustomizedButton>
            ) : null}
            {isPending ? null : (
              <Et3CustomizedButton
                isEnabled={!isChangeStatusPressed}
                withShadow={false}
                width={0}
                height={TILE_ACTION_HEIGHT}
                fDirection="row"
                alignItems="center"
                justifyContent="center"
                color={colors.all.white}
                style={{
                  flex: 1,
                  width: "auto",
                  minWidth: 0,
                  borderRadius: TILE_ACTION_RADIUS_CSS,
                  border: `1px solid ${
                    isChangeStatusPressed
                      ? colors.all.shadeOfGray
                      : colors.all.bondiBlue
                  }`,
                  cursor: isChangeStatusPressed ? "default" : "pointer"
                }}
                onClick={async () => {
                  await decide(
                    status === EApprovalItemStatus.APPROVED
                      ? EApprovalDecision.REJECTED
                      : EApprovalDecision.APPROVED
                  );
                  if (isPositiveNumber(pictureActionId)) {
                    getApproveRejectStore().setActionStatus(
                      PICTURE_ACTION_TYPE,
                      pictureActionId,
                      true
                    );
                  }
                }}
              >
                <FontAwesomeIcon
                  icon={faRotate}
                  color={
                    isChangeStatusPressed
                      ? colors.all.shadeOfGray
                      : colors.all.bondiBlue
                  }
                  title={i18n.get("CHANGE_STATUS")}
                  style={{
                    width: TILE_ACTION_ICON * unitX,
                    height: TILE_ACTION_ICON * unitY
                  }}
                />
              </Et3CustomizedButton>
            )}
          </React.Fragment>
        )}
      </ET3Div>
    </ET3Div>
  );
});
export default PictureTile;
